import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, CheckCircle2, Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fieldClass =
  "w-full rounded-xl bg-[rgba(8,12,22,0.7)] border border-[var(--line)] px-4 py-3 text-white placeholder:text-[var(--muted-2)] text-sm outline-none transition-colors duration-300 focus:border-[var(--green)] focus:ring-1 focus:ring-[var(--green)]";

export const LeadMagnetSection = () => {
  const { t, lang } = useLang();
  const m = t.leadMagnet;
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!emailRe.test(email.trim())) {
      toast.error(m.errEmail);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/checklist`, { email: email.trim(), company: company.trim() || null, language: lang });
      setDone(true);
      trackClick("lead_magnet");
    } catch (err) {
      toast.error(m.errFail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative scroll-mt-24 py-20 lg:py-24" data-testid="lead-magnet">
      <div className="mx-auto max-w-5xl px-5">
        <div className="relative overflow-hidden rounded-[2rem] panel p-8 lg:p-12">
          <div className="bg-grid bg-grid-fade absolute inset-0 opacity-40" />
          <div className="glow-orb animate-glow" style={{ width: 420, height: 260, top: -60, right: "8%", background: "var(--green)", opacity: 0.18 }} />
          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-[rgba(54,226,160,0.1)] text-[var(--green)]"><ClipboardCheck size={20} /></span>
                <span className="eyebrow">{m.eyebrow}</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white leading-tight" data-testid="lead-magnet-title">{m.title}</h2>
              <p className="mt-4 text-sm md:text-base text-white/90 leading-relaxed">{m.intro}</p>
              <ul className="mt-5 space-y-2.5">
                {m.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-white/90">
                    <Check size={15} className="text-[var(--green)] shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              {done ? (
                <div className="glass-card rounded-2xl p-8 text-center" data-testid="lead-magnet-success">
                  <div className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-[rgba(54,226,160,0.12)] text-[var(--green)] mb-4"><CheckCircle2 size={28} /></div>
                  <h3 className="font-display text-lg font-semibold text-white">{m.successTitle}</h3>
                  <p className="mt-2 text-sm text-muted">{m.successSub}</p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="glass-card rounded-2xl p-6 space-y-3" data-testid="lead-magnet-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={m.placeholder}
                    className={fieldClass}
                    data-testid="lead-magnet-email"
                  />
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder={m.companyPlaceholder}
                    className={fieldClass}
                    data-testid="lead-magnet-company"
                  />
                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" data-testid="lead-magnet-submit">
                    {loading ? (
                      <><Loader2 size={17} className="animate-spin" /> {m.sending}</>
                    ) : (
                      <>{m.submit} <ArrowUpRight size={16} /></>
                    )}
                  </button>
                  <p className="text-[11px] text-[var(--muted-2)] leading-relaxed text-center">{m.privacyNote}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
